import type { GameMode, LatLng, MissionDifficulty, SandboxConfig } from '@/state/types';
import {
  MISSION_DIFFICULTY_WEIGHTS,
  MISSION_INTERVAL_LEVEL_FACTOR,
  MISSION_INTERVAL_RANGES,
} from './missions';
import {
  MISSION_CATALOG,
  type MissionCatalogEntry,
  entriesOfDifficulty,
} from './missionCatalog';

/**
 * Pure helpers for the mission generator. Kept out of gameStore for testability.
 * All RNG can be injected so the generator behaviour is deterministic in tests.
 */

const TWO_PI = 2 * Math.PI;

/**
 * Sample a uniform-area random point inside a circle around `center`.
 * Algorithm: r = R*sqrt(u), θ = 2πv  → uniform over disk.
 */
export function spawnPosition(
  center: LatLng,
  radiusKm: number,
  rng: () => number = Math.random,
): LatLng {
  const r = radiusKm * Math.sqrt(rng());
  const theta = rng() * TWO_PI;
  // 1° latitude ≈ 111.32 km; longitude scales with cos(lat).
  const dLat = (r * Math.cos(theta)) / 111.32;
  const dLng =
    (r * Math.sin(theta)) / (111.32 * Math.cos((center.lat * Math.PI) / 180));
  return { lat: center.lat + dLat, lng: center.lng + dLng };
}

/**
 * Pick a difficulty bucket weighted by MISSION_DIFFICULTY_WEIGHTS.
 */
export function pickDifficulty(rng: () => number = Math.random): MissionDifficulty {
  const r = rng();
  let acc = 0;
  for (const [diff, weight] of Object.entries(MISSION_DIFFICULTY_WEIGHTS) as [
    MissionDifficulty,
    number,
  ][]) {
    acc += weight;
    if (r < acc) return diff;
  }
  return 'easy';
}

/**
 * Pick a random catalog entry of the given (or randomly chosen) difficulty.
 * Falls back through difficulty buckets if the chosen one is empty.
 *
 * Phase 10.7.3 — `policeMissionSharePct` biases the picker toward police
 * codes (P*); default 30 %. The bias is applied AFTER difficulty is chosen so
 * the difficulty distribution stays intact.
 */
export function pickCatalogEntry(
  rng: () => number = Math.random,
  policeMissionSharePct = 30,
): MissionCatalogEntry {
  const difficulty = pickDifficulty(rng);
  let pool = entriesOfDifficulty(difficulty);
  if (pool.length === 0) {
    const order: MissionDifficulty[] = ['easy', 'medium', 'hard', 'expert', 'extreme'];
    for (const d of order) {
      pool = entriesOfDifficulty(d);
      if (pool.length > 0) break;
    }
    if (pool.length === 0) return MISSION_CATALOG[0];
  }

  // Apply police-share bias.
  const wantPolice = rng() * 100 < policeMissionSharePct;
  const policePool = pool.filter((e) => e.category === 'police');
  const nonPolicePool = pool.filter((e) => e.category !== 'police');
  let chosen: MissionCatalogEntry[];
  if (wantPolice && policePool.length > 0) {
    chosen = policePool;
  } else if (!wantPolice && nonPolicePool.length > 0) {
    chosen = nonPolicePool;
  } else {
    chosen = pool;
  }
  return chosen[Math.floor(rng() * chosen.length)];
}

/**
 * Compute the next spawn interval in milliseconds (gameTime).
 * Level scaling: factor = max(0.6, 1 - level * 0.02). At L20 → 0.6× (40 % faster).
 */
export function intervalMs(
  mode: GameMode,
  level: number,
  sandbox: SandboxConfig,
  rng: () => number = Math.random,
): number {
  let baseSec: number;
  if (mode === 'sandbox') {
    baseSec = sandbox.missionIntervalSec;
  } else {
    const { minSec, maxSec } = MISSION_INTERVAL_RANGES[mode];
    baseSec = minSec + rng() * (maxSec - minSec);
  }
  const factor = Math.max(0.6, 1 - level * MISSION_INTERVAL_LEVEL_FACTOR);
  return Math.round(baseSec * 1000 * factor);
}

/** Active = open / dispatched / onscene. Used to gate spawning. */
export function isActiveMissionStatus(
  status: 'open' | 'dispatched' | 'onscene' | 'closed' | 'failed',
): boolean {
  return status === 'open' || status === 'dispatched' || status === 'onscene';
}
